import { useState } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { Leaf, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { NotificationBell } from './NotificationBell';

export const PortalLayout = ({ title, subtitle, navItems, homeLink = '/' }) => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => { await logout(); navigate('/'); };

  const NavList = () => (
    <nav className="space-y-1" data-testid="portal-nav">
      {navItems.map((item) => {
        const active = location.pathname === item.to;
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={() => setOpen(false)}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl font-manrope text-sm font-medium transition-colors ${active ? 'bg-forest text-cream' : 'text-dark/70 hover:bg-forest/5 hover:text-forest'}`}
            data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]/g, '-')}`}
          >
            <item.icon className="w-5 h-5 flex-shrink-0" />
            <span>{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-cream" data-testid="portal-layout">
      {/* Sidebar - desktop */}
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-white border-r border-forest/10 z-40">
        <Link to={homeLink} className="flex items-center gap-2 px-6 h-20 border-b border-forest/10">
          <div className="w-9 h-9 bg-forest rounded-full flex items-center justify-center"><Leaf className="w-5 h-5 text-cream" /></div>
          <div><span className="font-playfair text-xl font-bold text-dark block leading-none">FarmBox</span><span className="text-[10px] uppercase tracking-widest text-forest font-bold">{subtitle}</span></div>
        </Link>
        <div className="flex-1 overflow-y-auto p-4"><NavList /></div>
        <button onClick={handleLogout} className="flex items-center gap-3 px-6 py-4 border-t border-forest/10 text-dark/60 hover:text-red-600 font-manrope text-sm" data-testid="portal-logout"><LogOut className="w-5 h-5" />Log out</button>
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-dark/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white flex flex-col">
            <div className="flex items-center justify-between px-6 h-20 border-b border-forest/10">
              <span className="font-playfair text-xl font-bold text-dark">FarmBox</span>
              <button onClick={() => setOpen(false)}><X className="w-6 h-6 text-dark" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4"><NavList /></div>
            <button onClick={handleLogout} className="flex items-center gap-3 px-6 py-4 border-t border-forest/10 text-red-600 font-manrope text-sm"><LogOut className="w-5 h-5" />Log out</button>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 h-16 bg-white/90 backdrop-blur border-b border-forest/10 flex items-center justify-between px-4 md:px-8">
          <div className="flex items-center gap-3">
            <button className="lg:hidden p-2" onClick={() => setOpen(true)} data-testid="portal-menu-toggle"><Menu className="w-6 h-6 text-dark" /></button>
            <h1 className="font-playfair text-xl md:text-2xl font-bold text-dark" data-testid="portal-title">{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="hidden sm:flex flex-col items-end">
              <span className="font-manrope text-sm font-semibold text-dark leading-none">{user?.name}</span>
              <span className="font-manrope text-xs text-dark/50">{user?.email}</span>
            </div>
          </div>
        </header>
        <main className="p-4 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
};
